import { deviceService } from "core/features/device/device.service";
import { DeviceType } from "core/features/device/enums/device-type.enum";
import { BannerFiltersAreaComponent } from "./banner-filters-area.component";
import { BannerFilterComponent } from "./banner-filter.component";
import { BannersDetailsComponent } from "./banners-details.component";
import { BannersModel } from "./models/banners.model";

export class BannersService {
    private _currentContext = $();

    private _selectedBannerId: string = null;

    private _banners: Array<BannersModel> = [];

    private _bannerFiltersArea: BannerFiltersAreaComponent = null;
    private _bannerFilters: Array<BannerFilterComponent> = [];

    private _bannerDetailsDesktop: BannersDetailsComponent = null;
    private _bannerDetailsListMobile: Array<BannersDetailsComponent> = [];

    private setQueryString(bannerId: string): void {
        const queryParams = new URLSearchParams(window.location.search);
        queryParams.set("banner", bannerId);
        history.replaceState(null, null, "?" + queryParams.toString());
    }

    private setInitialState(): void {
        if (!this._banners.length) {
            return;
        }

        const queryStringValues = Object.fromEntries(new URLSearchParams(window.location.search).entries());
        const queryStringBannerId = queryStringValues.banner?.toString();

        const bannersIds = this._banners.map(banner => banner.id);

        const isMobile = deviceService.checkDeviceType() === DeviceType.Mobile;

        if(queryStringBannerId) {
            this._selectedBannerId = queryStringBannerId;

            const bannerExists = queryStringBannerId && bannersIds.includes(this._selectedBannerId);

            if(bannerExists) {
                this.setActiveFilter(this._selectedBannerId);
                this.showBannerDetails(this._selectedBannerId, isMobile);
                if(isMobile)
                {
                    this.scrollToActiveFilter();
                }
                return;
            }
        }

        if(bannersIds.length) {
            this._selectedBannerId = bannersIds[0];

            this.setActiveFilter(this._selectedBannerId);
            this.showBannerDetails(this._selectedBannerId, isMobile);
        }
    }

    private setActiveFilter(bannerId: string): void {
        this._bannerFilters.forEach(filter => {
            const isActive = filter.bannerId === bannerId;
            filter.toggleActive(isActive);
        });
    }

    private showBannerDetails(bannerId: string, isMobile: boolean) : void {
        this._selectedBannerId = bannerId;

        const banners = this._banners.find(c => c.id === this._selectedBannerId)?.areas;

        if (banners && banners.length) {
            this._bannerFiltersArea.handleFiltersVisibility(this._banners.length === 1 && !isMobile);
            if (isMobile) {
                const bannerDetailsMobile = this._bannerDetailsListMobile.find(b => b.bannerId === this._selectedBannerId);
                this._bannerDetailsDesktop.destroy();

                this._bannerDetailsListMobile.forEach(bannerDetails => {
                    const isActive = bannerDetails.bannerId === bannerId;
                    bannerDetails.toggleActive(isActive);
                });
                bannerDetailsMobile.setBanners(banners).render();
            } else {
                this._bannerDetailsDesktop.setBanners(banners).render();
            }
        }
    }

    private scrollToActiveFilter() : void {
        const activeFilter = this._bannerFilters.find(f => f.bannerId === this._selectedBannerId);
        if(activeFilter)
        {
            activeFilter.scrollTo();
        }
    }

    private parseContext(): void {
        const bannerLinks = $(BannerFilterComponent.SELECTOR, this._currentContext);
        bannerLinks.each((index, entry) => {
            const linkContext = $(entry);
            const link = new BannerFilterComponent();
            link.init(linkContext);

            this._bannerFilters.push(link);
        });

        const bannerFiltersArea = $(BannerFiltersAreaComponent.SELECTOR, this._currentContext);
        this._bannerFiltersArea = new BannerFiltersAreaComponent();
        this._bannerFiltersArea.init(bannerFiltersArea);

        const bannerDetailsDesktop = $(BannersDetailsComponent.SELECTOR_DESKTOP, this._currentContext);
        this._bannerDetailsDesktop = new BannersDetailsComponent();
        this._bannerDetailsDesktop.init(bannerDetailsDesktop);

        const bannersDetailsMobile = $(BannersDetailsComponent.SELECTOR_MOBILE, this._currentContext);
        bannersDetailsMobile.each((index, entry) => {
            const bannerDetailsMobileContext = $(entry);
            const bannerDetailsMobile = new BannersDetailsComponent();
            bannerDetailsMobile.init(bannerDetailsMobileContext);

            this._bannerDetailsListMobile.push(bannerDetailsMobile);
        });
    }

    private bindEvents(): void {
        this._bannerFilters.forEach(filter => {
            filter.on("click").subscribe((bannerId: string) => {
                const isMobile = deviceService.checkDeviceType() === DeviceType.Mobile;

                this.setActiveFilter(bannerId);
                this.showBannerDetails(bannerId, isMobile);
                this.setQueryString(bannerId);

                if(isMobile)
                {
                    this.scrollToActiveFilter();
                }
            });
        });

        deviceService.deviceType.subscribe((deviceType: DeviceType) => {
            if (this._selectedBannerId) {
                this.showBannerDetails(this._selectedBannerId, deviceType === DeviceType.Mobile);
            }
        });
    }

    public init(context: JQuery, banners: Array<BannersModel>): void {
        this._currentContext = context || this._currentContext;
        this._banners = banners;

        this.parseContext();
        this.bindEvents();

        this.setInitialState();
    }
}
